import app from 'flarum/forum/app';
import { extend as _rawExtend, override as _rawOverride } from 'flarum/common/extend';
import UserPage from 'flarum/forum/components/UserPage';
import PostsUserPage from 'flarum/forum/components/PostsUserPage';
import DiscussionsUserPage from 'flarum/forum/components/DiscussionsUserPage';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import { safeExtend, safeOverride } from './utils/safeExtend';

/**
 * (辉哥 2026-09-09 19:25 反馈 "用户页改动迁到 ziven core"):
 *   把 ziven-bjxy-website 整段迁过来
 *   行为:
 *     1. 默认进入用户页 (/u/{username}) 显示"讨论"列表 (DiscussionList) 而非"回复"列表
 *     2. 非 admin/mod 用户隐藏"回复" tab (侧边栏 nav + mobile top bar dropdown 都不显示)
 *     3. mobile top bar dropdown 显示当前 active tab 文字 (e.g. "讨论 2")
 *     4. admin/mod 端因 vendor postsItem.href=/u/{username} 默认 active=true
 *        → 显式设 postsItem.attrs.active=false 让 SelectDropdown 跳过 posts 选 discussions
 *
 *   根因 (c.f.n):
 *     vendor PostsUserPage.route.path = /u/{username} (默认用户页)
 *     进入 /u/Ziven 走 PostsUserPage 渲染用户的"回复"列表
 *     bjxy 业务决定: 默认显示"讨论"列表 (DiscussionListState 拉用户发起的讨论)
 *     vendor navItems 4 个: posts (100) / discussions (90) / settings (-90) / security (-100)
 *
 *   失败链 (c.f.n.a-d 全部撤回):
 *     a) 劫持 m.route.set: mithril boot 不调 m.route.set, 劫持错过初次 mount
 *     b) initializer 阶段覆盖 app.routes.user.component: 跑在 m.route() 之后, 覆盖无效
 *     c) import 阶段立即覆盖: Puppeteer console 显示覆盖生效但实际 mount 仍 PostsUserPage
 *     d) extend PostsUserPage.prototype.content: extend 是 mutate output, 不是 replace
 *     e) prototype 直接赋值: 100% replace vendor 方法 ✓
 *
 *   实战教训 (c.f.o.b / c.f.p / c.f.q):
 *     - vendor Flarum 2.0 App.less L104 phone 视口下 .App-titleControl .Button-label { display: none }
 *       → mobile top bar 只显示双箭头图标, 不显示 active tab 文字
 *     - vendor LinkButton.isActive (LinkButton.js L43-47) 优先 typeof attrs.active !== 'undefined'
 *       → 显式设 true/false 都能覆盖 vendor URL 比对
 *     - vendor UserPage.tsx L153 postsItem.attrs.href = app.route('user.posts') = /u/{username}
 *       → admin 进默认用户页 vendor URL 比对自动 active=true, 必须显式设 false 才能让 SelectDropdown 跳过
 *     - SelectDropdown.getButtonContent L49 children.find(isActive) 选第一个 active
 *       → priority 100 (posts) > 90 (discussions), admin 端不强制设 false 就会渲染 '回复 2'
 *     - wikimedia/less.php 5.x 严格 parse, bjxy 顶部 shim @media @phone 段可能在 CSS 输出里被丢
 *       → 改用 hardcoded @media (max-width: 767px) 绕过
 */
export default function modifyUserPage() {
  // (辉哥 9/17 16:47 派 Coder 修): webpack DCE 把 const helper globals 去掉,
  // 改用 function declaration 让 helper 名字在函数体内可用

  // 1. extend UserPage.navItems: 删 posts tab 给非 admin/mod + 显式设 active 状态覆盖 vendor URL 比对
  safeExtend(UserPage, 'navItems', function (items) {
    const sessionUser = app.session.user;
    const isAdminOrMod = sessionUser && (sessionUser.isAdmin() || sessionUser.isModerator());
    if (!isAdminOrMod) {
      items.remove('posts');
    }
    // 强制设 discussions item.attrs.active = true
    // 原因: vendor LinkButton.isActive L46 优先 attrs.active, 显式设 true 后
    //   SelectDropdown Button-labelText 渲染 '讨论 2' 文字 (跟辉哥期望 "显示当前 active tab 文字" 一致)
    //   普通用户端 vendor URL 不匹配 (/u/Ziven vs /u/Ziven/discussions), 没 c.f.p 会渲染空字符串
    //   admin 端 vendor URL 也不匹配 (posts 是 /u/Ziven, discussions 是 /u/Ziven/discussions)
    const discussionsItem = items.get('discussions');
    if (discussionsItem && discussionsItem.attrs) {
      discussionsItem.attrs.active = true;
    }
    // admin/mod 端强制设 posts.active=false
    // 原因: vendor UserPage.tsx L153 postsItem.attrs.href = app.route('user.posts') = /u/{username}
    //   admin 进 /u/Ziven, m.route.get() = /u/Ziven 跟 postsItem.href 完美匹配 → posts.active=true
    //   c.f.p 强制 discussions.active=true → 两个 tab 都 active
    //   priority 100 (posts) > 90 (discussions)
    //   SelectDropdown.find(isActive) 选第一个 → 渲染 '回复 2' 而不是 '讨论 2'
    // 修法: admin/mod 端显式设 postsItem.attrs.active = false
    //   vendor LinkButton.isActive L46 读到 false 直接返回 false (不读 URL 比对)
    //   SelectDropdown.find 跳过 posts → 选 discussions → '讨论 2'
    //   普通用户走 c.f.n items.remove('posts'), items.get('posts') 返回 undefined, 跳过
    if (isAdminOrMod) {
      const postsItem = items.get('posts');
      if (postsItem && postsItem.attrs) {
        postsItem.attrs.active = false;
      }
    }
  });

  // 2. 覆盖 PostsUserPage.prototype.content/oninit/show: 默认渲染 DiscussionList 而不是 PostsUserPage 原版
  // 原因: 跟 navItems 配套 — 删了 posts tab + 强制 discussions active 后, 实际页面内容也要是讨论列表
  //   prototype 直接赋值 100% replace vendor 方法 (extend 是 mutate output 不是 replace)
  //   顺序关键: bjxy oninit 先设 discussionsState 再调 vendor oninit
  //   否则 vendor oninit 内部调 super.oninit → UserPage.oninit → loadUser → 同步调 this.show(user)
  //   这时 bjxy oninit 还没设 discussionsState, my show override 看到 undefined
  try {
    const vendorContent = PostsUserPage.prototype.content;
    const vendorOninit = PostsUserPage.prototype.oninit;
    const vendorShow = PostsUserPage.prototype.show;
    PostsUserPage.prototype.content = function () {
      return m("div", {
        className: "DiscussionsUserPage"
      }, m(DiscussionList, {
        state: this.discussionsState
      }));
    };
    PostsUserPage.prototype.oninit = function (vnode) {
      if (!this.discussionsState) {
        this.discussionsState = new DiscussionListState({
          sort: 'newest'
        }, 20);
      }
      vendorOninit.call(this, vnode);
    };
    PostsUserPage.prototype.show = function (user) {
      vendorShow.call(this, user);
      if (this.discussionsState) {
        this.discussionsState.refreshParams({
          filter: {
            author: user.slug()
          },
          sort: 'newest'
        }, 1);
        this.discussionsState.refresh();
      }
    };
  } catch (e) {
    console.warn('[ziven-core] modifyUserPage failed:', e && e.message);
  }
}